import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Axios from "axios";
import Select from "react-select";
import AuthContext from "../context/AuthContext";
import { API_URL } from "../components/api";

const EMPTY_MARKUPS = {
  single_markup: "0",
  double_markup: "0",
  triple_markup: "0",
  single_reset_markup: "0",
  double_reset_markup: "0",
  triple_reset_markup: "0",
  single_full_markup: "0",
  double_full_markup: "0",
  triple_full_markup: "0",
};

const DOUBLE_TRIPLE_FIELDS = new Set([
  "double_markup",
  "triple_markup",
  "double_reset_markup",
  "triple_reset_markup",
  "double_full_markup",
  "triple_full_markup",
]);

const PRICING_ROWS = [
  {
    field: "single_markup",
    label: "Single Recharge",
  },
  {
    field: "single_reset_markup",
    label: "Single Reset",
  },
  {
    field: "single_full_markup",
    label: "Single Full",
  },
  {
    field: "double_markup",
    label: "Double Recharge",
    doubleOrTriple: true,
  },
  {
    field: "double_reset_markup",
    label: "Double Reset",
    doubleOrTriple: true,
  },
  {
    field: "double_full_markup",
    label: "Double Full",
    doubleOrTriple: true,
  },
  {
    field: "triple_markup",
    label: "Triple Recharge",
    doubleOrTriple: true,
  },
  {
    field: "triple_reset_markup",
    label: "Triple Reset",
    doubleOrTriple: true,
  },
  {
    field: "triple_full_markup",
    label: "Triple Full",
    doubleOrTriple: true,
  },
];

const formatMoney = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "0.00";
  }

  return amount.toFixed(2);
};

const markupValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "0";
  }

  return String(value);
};

const markupsFromDetails = (details) => {
  const values = {};

  Object.keys(EMPTY_MARKUPS).forEach((field) => {
    values[field] = markupValue(details?.[field]);
  });

  return values;
};

function Formp({ details }) {
  const { user } = useContext(AuthContext);

  const authTokens = JSON.parse(
    localStorage.getItem("authTokens")
  );

  const [dealers, setDealers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [client, setClient] = useState("");
  const [planId, setPlanId] = useState("");
  const [markups, setMarkups] = useState({
    ...EMPTY_MARKUPS,
  });
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] =
    useState("success");
  const [submitting, setSubmitting] =
    useState(false);

  useEffect(() => {
    setClient(details?.child_id || "");
    setPlanId(details?.internet_plan_id || "");
    setMarkups(markupsFromDetails(details));
    setMessage("");
  }, [details]);

  useEffect(() => {
    let isMounted = true;

    const loadOptions = async () => {
      try {
        const headers = {
          Authorization:
            `Bearer ${authTokens.access}`,
          "Content-Type": "application/json",
        };

        const [
          dealerResponse,
          planResponse,
        ] = await Promise.all([
          Axios.get(
            `${API_URL}/administration/dealers/`,
            {
              headers,
              params: {
                username: user.username,
              },
            }
          ),
          Axios.get(
            `${API_URL}/radius/plans/internet`,
            {
              headers,
              params: {
                username: user.username,
              },
            }
          ),
        ]);

        if (isMounted) {
          setDealers(dealerResponse.data);
          setPlans(planResponse.data);
        }
      } catch (error) {
        console.error(error);

        if (isMounted) {
          setMessageType("error");
          setMessage(
            "The dealer and plan options could not be loaded."
          );
        }
      }
    };

    loadOptions();

    return () => {
      isMounted = false;
    };
  }, [
    authTokens.access,
    user.username,
  ]);

  const dealerOptions = useMemo(
    () =>
      dealers.map((item) => ({
        label: item.fullname_en,
        value: item.dealer_id,
      })),
    [dealers]
  );

  const planOptions = useMemo(
    () =>
      plans.map((item) => ({
        label: item.plan_name,
        value: item.internet_plan_id,
        cost: item.cost,
      })),
    [plans]
  );

  const selectedDealer =
    dealerOptions.find(
      (option) =>
        Number(option.value) === Number(client)
    ) ||
    (client
      ? {
          label: details?.dealer_en || "Current dealer",
          value: client,
        }
      : null);

  const selectedPlan =
    planOptions.find(
      (option) =>
        Number(option.value) === Number(planId)
    ) ||
    (planId
      ? {
          label: details?.plan_name || "Current plan",
          value: planId,
          cost: details?.cost || 0,
        }
      : null);

  const planName = String(
    selectedPlan?.label || ""
  )
    .trim()
    .toUpperCase();

  const basicOrLight = [
    "BASIC",
    "LIGHT",
  ].includes(planName);

  const planCost = Number(
    selectedPlan?.cost || 0
  );

  const fixedPrices = {
    single_markup: planCost,
    single_reset_markup:
      planCost * 0.08,
    single_full_markup:
      planCost * 0.25,

    double_markup:
      planCost * 1.75,
    double_reset_markup:
      planCost * 1.75 * 0.08,
    double_full_markup:
      planCost * 1.75 * 0.25,

    triple_markup:
      planCost * 2.5,
    triple_reset_markup:
      planCost * 3.25 * 0.08,
    triple_full_markup:
      planCost * 3.25 * 0.25,
  };

  const visiblePricingRows =
    PRICING_ROWS.filter(
      (row) =>
        !basicOrLight ||
        !row.doubleOrTriple
    );

  const validate = () => {
    if (
      !details?.reseller_plan_id ||
      client === "" ||
      planId === "" ||
      selectedPlan === null
    ) {
      return false;
    }

    return visiblePricingRows.every(
      (row) => {
        const value = markups[row.field];

        return (
          String(value).trim() !== "" &&
          Number.isFinite(Number(value)) &&
          Number(value) >= 0
        );
      }
    );
  };

  const handleMarkupChange = (
    field,
    value
  ) => {
    setMarkups((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate() || submitting) {
      return;
    }

    const submittedMarkups = {
      ...markups,
    };

    if (basicOrLight) {
      DOUBLE_TRIPLE_FIELDS.forEach(
        (field) => {
          submittedMarkups[field] = "0";
        }
      );
    }

    setSubmitting(true);
    setMessage("");

    try {
      const response = await Axios.post(
        `${API_URL}/radius/plans/resellers/`,
        {
          Action: "Put",
          reseller_id:
            details.reseller_plan_id,
          child_id: client,
          plan_id: planId,
          userdealer: user.username,
          ...submittedMarkups,
        },
        {
          headers: {
            Authorization:
              `Bearer ${authTokens.access}`,
          },
        }
      );

      setMessageType("success");
      setMessage(
        typeof response.data === "string"
          ? response.data
          : "The plan was updated successfully."
      );

      setTimeout(() => {
        window.location.reload(false);
      }, 1200);
    } catch (error) {
      console.error(error);

      const responseData =
        error.response?.data;

      const errorMessage =
        responseData?.error ||
        responseData?.message ||
        (typeof responseData === "string"
          ? responseData
          : error.message ||
            "Unknown error");

      setMessageType("error");
      setMessage(
        `Error updating plan: ${errorMessage}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mod reseller-plan-pricing-form reseller-plan-edit-form">
      <form onSubmit={handleSubmit}>
        <div className="reseller-plan-original-assignment">
          <span>
            Current dealer:
            <strong>
              {details?.dealer_en || "-"}
            </strong>
          </span>

          <span>
            Current plan:
            <strong>
              {details?.plan_name || "-"}
            </strong>
          </span>
        </div>

        <div className="reseller-plan-selection-grid">
          <div className="reseller-plan-form-group">
            <label htmlFor="edit-reseller-plan-dealer">
              Dealer Name
            </label>

            <Select
              inputId="edit-reseller-plan-dealer"
              classNamePrefix="reseller-price-select"
              placeholder="Choose Dealer"
              options={dealerOptions}
              value={selectedDealer}
              onChange={(option) => {
                setClient(
                  option ? option.value : ""
                );
                setMessage("");
              }}
            />
          </div>

          <div className="reseller-plan-form-group">
            <label htmlFor="edit-reseller-plan-plan">
              Plan Name
            </label>

            <Select
              inputId="edit-reseller-plan-plan"
              classNamePrefix="reseller-price-select"
              placeholder="Choose Plan"
              options={planOptions}
              value={selectedPlan}
              onChange={(option) => {
                setPlanId(
                  option ? option.value : ""
                );
                setMessage("");
              }}
            />
          </div>
        </div>

        {selectedPlan && (
          <>
            <div className="reseller-plan-selected-summary">
              <span>
                Editing plan:
                <strong>
                  {selectedPlan.label}
                </strong>
              </span>

              <span>
                OSS base cost:
                <strong>
                  ${formatMoney(planCost)}
                </strong>
              </span>
            </div>

            {basicOrLight && (
              <div className="reseller-plan-restriction">
                Double and Triple pricing are not available
                for BASIC or LIGHT plans.
              </div>
            )}

            <div className="reseller-plan-pricing-table">
              <div className="reseller-plan-pricing-header">
                <span>Pricing Item</span>
                <span>OSS Fixed</span>
                <span>Reseller Extra</span>
                <span>Dealer Total</span>
              </div>

              {visiblePricingRows.map((row) => {
                const fixedAmount =
                  fixedPrices[row.field];

                const enteredMarkup = Number(
                  markups[row.field] || 0
                );

                const safeMarkup =
                  Number.isFinite(enteredMarkup)
                    ? enteredMarkup
                    : 0;

                const total =
                  fixedAmount + safeMarkup;

                return (
                  <div
                    className="reseller-plan-pricing-row"
                    key={row.field}
                  >
                    <span className="reseller-plan-pricing-label">
                      {row.label}
                    </span>

                    <output>
                      ${formatMoney(fixedAmount)}
                    </output>

                    <div className="reseller-plan-markup-input">
                      <span>$</span>

                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={markups[row.field]}
                        required
                        aria-label={`${row.label} reseller extra`}
                        onChange={(event) =>
                          handleMarkupChange(
                            row.field,
                            event.target.value
                          )
                        }
                      />
                    </div>

                    <output className="reseller-plan-total">
                      ${formatMoney(total)}
                    </output>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {message && (
          <div
            className={`message ${messageType}`}
          >
            {message}
          </div>
        )}

        <div className="fo">
          <button
            type="submit"
            className="btn btn-primary btn-md"
            disabled={
              !validate() || submitting
            }
          >
            {submitting ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Formp;
